/**
 * hooks/useAdminAnalytics.ts
 *
 * Fetches usage analytics for the admin monitoring dashboard.
 *
 * Data sources:
 * - vw_user_activity_summary: per-user counts (non-admin, non-deleted users only)
 * - requests.first_opened_at: true client opening (write-once, deduplicated at DB level)
 * - requests.completed_at:    client submission
 * - clients table:            clients_created delta (prev period, deleted_at IS NULL to match clients_agg CTE)
 * - user_activity_events:     requests_sent delta (prev period)
 *
 * DateRange supports 'today' (since midnight UTC), 7, 30, or 90 days.
 * Sessions rule: one session per 60-minute gap (enforced by log_user_event RPC).
 * requests_sent: every click on "Copy link" or "Send email" (no dedup by request_id).
 *
 * Updated May 2026: added 'today' filter, requests_sent metric, removed dailyTimeline
 * (chart removed). Session rule updated to 1h gap.
 * Updated May 2026: added previousValues for all 5 metrics, deltas for requestsSent
 * and clientsCreated, comparison label helpers.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';

export type DateRange = 'today' | 7 | 30 | 90;

export interface UserActivityRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  signup_at: string;
  last_activity_at: string | null;
  sessions: number;
  templates_created: number;
  clients_created: number;
  requests_opened: number;
  requests_completed: number;
  requests_sent: number;
}

export interface KpiDeltas {
  activeUsers: number | null;
  clientsCreated: number | null;
  requestsSent: number | null;
  requestsOpened: number | null;
  requestsCompleted: number | null;
  clientResponseRate: number | null;
}

/** Raw values from the previous comparison period, used for tooltip labels. */
export interface KpiPreviousValues {
  activeUsers: number;
  clientsCreated: number;
  requestsSent: number;
  requestsOpened: number;
  requestsCompleted: number;
}

export interface AdminAnalyticsData {
  activeUsers: number;
  requestsSent: number;
  requestsOpened: number;
  requestsCompleted: number;
  clientsCreated: number;
  clientResponseRate: number | null;
  deltas: KpiDeltas;
  previousValues: KpiPreviousValues;
  users: UserActivityRow[];
  lastRefreshedAt: Date;
}

const EMPTY: AdminAnalyticsData = {
  activeUsers: 0,
  requestsSent: 0,
  requestsOpened: 0,
  requestsCompleted: 0,
  clientsCreated: 0,
  clientResponseRate: null,
  deltas: {
    activeUsers: null, clientsCreated: null, requestsSent: null,
    requestsOpened: null, requestsCompleted: null, clientResponseRate: null,
  },
  previousValues: { activeUsers: 0, clientsCreated: 0, requestsSent: 0, requestsOpened: 0, requestsCompleted: 0 },
  users: [],
  lastRefreshedAt: new Date(),
};

type RawUserRow = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  signup_at: string;
  last_activity_at: string | null;
  sessions_today: number; sessions_7d: number; sessions_30d: number; sessions_90d: number;
  templates_created_today: number; templates_created_7d: number; templates_created_30d: number; templates_created_90d: number;
  clients_created_today: number; clients_created_7d: number; clients_created_30d: number; clients_created_90d: number;
  requests_opened_today: number; requests_opened_7d: number; requests_opened_30d: number; requests_opened_90d: number;
  requests_completed_today: number; requests_completed_7d: number; requests_completed_30d: number; requests_completed_90d: number;
  requests_sent_today: number; requests_sent_7d: number; requests_sent_30d: number; requests_sent_90d: number;
};

type RawOpenedRow    = { first_opened_at: string | null };
type RawCompletedRow = { completed_at: string | null };
type RawPrevSessionRow = { user_id: string };

function pick(row: RawUserRow, key: string, range: DateRange): number {
  const suffix = range === 'today' ? 'today' : `${range}d`;
  return (row as unknown as Record<string, number>)[`${key}_${suffix}`] ?? 0;
}

/** Returns ISO string for midnight UTC today */
function todayMidnightIso(): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

/** Returns ISO string for midnight UTC N days ago */
function daysAgoIso(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString();
}

function getSinceBounds(dateRange: DateRange): { sinceIso: string; prevSinceIso: string } {
  if (dateRange === 'today') {
    const sinceIso = todayMidnightIso();
    const yesterday = new Date();
    yesterday.setUTCHours(0, 0, 0, 0);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);
    return { sinceIso, prevSinceIso: yesterday.toISOString() };
  }
  return { sinceIso: daysAgoIso(dateRange), prevSinceIso: daysAgoIso(dateRange * 2) };
}

function calcRate(completed: number, opened: number): number | null {
  if (opened <= 0) return null;
  return Math.round((completed / opened) * 100);
}

function chunk<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

const CHUNK_SIZE = 300;

async function fetchChunkedPrevPeriod(
  userIdChunks: string[][],
  prevSinceIso: string,
  sinceIso: string
): Promise<{
  prevOpened: number;
  prevCompleted: number;
  prevSent: number;
  prevClientsCreated: number;
  prevActiveUserIds: Set<string>;
}> {
  const results = await Promise.all(
    userIdChunks.map((ids) =>
      Promise.all([
        supabase.from('requests').select('first_opened_at', { count: 'exact', head: true })
          .not('first_opened_at', 'is', null)
          .gte('first_opened_at', prevSinceIso).lt('first_opened_at', sinceIso)
          .in('user_id', ids),
        supabase.from('requests').select('completed_at', { count: 'exact', head: true })
          .not('completed_at', 'is', null)
          .gte('completed_at', prevSinceIso).lt('completed_at', sinceIso)
          .in('user_id', ids),
        supabase.from('user_activity_events').select('user_id')
          .eq('event_type', 'session_started')
          .gte('created_at', prevSinceIso).lt('created_at', sinceIso)
          .in('user_id', ids),
        supabase.from('user_activity_events').select('id', { count: 'exact', head: true })
          .eq('event_type', 'request_sent')
          .gte('created_at', prevSinceIso).lt('created_at', sinceIso)
          .in('user_id', ids),
        supabase.from('clients').select('id', { count: 'exact', head: true })
          .gte('created_at', prevSinceIso).lt('created_at', sinceIso)
          .in('user_id', ids)
          .is('deleted_at', null),
      ])
    )
  );

  let prevOpened = 0;
  let prevCompleted = 0;
  let prevSent = 0;
  let prevClientsCreated = 0;
  const prevActiveUserIds = new Set<string>();

  for (const [prevOpenedRes, prevCompletedRes, prevSessionsRes, prevSentRes, prevClientsRes] of results) {
    if (prevOpenedRes.error) throw prevOpenedRes.error;
    if (prevCompletedRes.error) throw prevCompletedRes.error;
    if (prevSessionsRes.error) throw prevSessionsRes.error;
    if (prevSentRes.error) throw prevSentRes.error;
    if (prevClientsRes.error) throw prevClientsRes.error;
    prevOpened += prevOpenedRes.count ?? 0;
    prevCompleted += prevCompletedRes.count ?? 0;
    prevSent += prevSentRes.count ?? 0;
    prevClientsCreated += prevClientsRes.count ?? 0;
    for (const r of (prevSessionsRes.data ?? []) as RawPrevSessionRow[]) {
      prevActiveUserIds.add(r.user_id);
    }
  }

  return { prevOpened, prevCompleted, prevSent, prevClientsCreated, prevActiveUserIds };
}

export function useAdminAnalytics(dateRange: DateRange) {
  const [data, setData] = useState<AdminAnalyticsData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    requestIdRef.current += 1;
    const myId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const { sinceIso, prevSinceIso } = getSinceBounds(dateRange);

      // Step 1: fetch the user summary view (non-admin, non-deleted only)
      const usersRes = await supabase.from('vw_user_activity_summary').select('*');
      if (myId !== requestIdRef.current) return;
      if (usersRes.error) throw usersRes.error;

      const rawUsers = (usersRes.data ?? []) as RawUserRow[];
      const users: UserActivityRow[] = rawUsers.map((r) => ({
        id: r.id, email: r.email, first_name: r.first_name, last_name: r.last_name,
        signup_at: r.signup_at, last_activity_at: r.last_activity_at,
        sessions: pick(r, 'sessions', dateRange),
        templates_created: pick(r, 'templates_created', dateRange),
        clients_created: pick(r, 'clients_created', dateRange),
        requests_opened: pick(r, 'requests_opened', dateRange),
        requests_completed: pick(r, 'requests_completed', dateRange),
        requests_sent: pick(r, 'requests_sent', dateRange),
      }));

      const userIdChunks = chunk(rawUsers.map((r) => r.id), CHUNK_SIZE);

      // Step 2: fetch prev-period data for delta badges
      const prevPeriodData = await fetchChunkedPrevPeriod(userIdChunks, prevSinceIso, sinceIso);

      if (myId !== requestIdRef.current) return;

      const requestsSent      = users.reduce((s, u) => s + u.requests_sent, 0);
      const requestsOpened    = users.reduce((s, u) => s + u.requests_opened, 0);
      const requestsCompleted = users.reduce((s, u) => s + u.requests_completed, 0);
      const activeUsers       = users.filter((u) => u.sessions > 0).length;
      const clientsCreated    = users.reduce((s, u) => s + u.clients_created, 0);
      const clientResponseRate = calcRate(requestsCompleted, requestsOpened);

      const prevActiveUsers = prevPeriodData.prevActiveUserIds.size;
      const { prevOpened, prevCompleted, prevSent, prevClientsCreated } = prevPeriodData;
      const prevRate = calcRate(prevCompleted, prevOpened);

      const deltas: KpiDeltas = {
        activeUsers: activeUsers - prevActiveUsers,
        clientsCreated: clientsCreated - prevClientsCreated,
        requestsSent: requestsSent - prevSent,
        requestsOpened: requestsOpened - prevOpened,
        requestsCompleted: requestsCompleted - prevCompleted,
        clientResponseRate: clientResponseRate !== null && prevRate !== null
          ? clientResponseRate - prevRate : null,
      };

      const previousValues: KpiPreviousValues = {
        activeUsers: prevActiveUsers,
        clientsCreated: prevClientsCreated,
        requestsSent: prevSent,
        requestsOpened: prevOpened,
        requestsCompleted: prevCompleted,
      };

      if (myId !== requestIdRef.current) return;
      setData({ activeUsers, requestsSent, requestsOpened, requestsCompleted, clientsCreated, clientResponseRate, deltas, previousValues, users, lastRefreshedAt: new Date() });
    } catch (err) {
      if (myId !== requestIdRef.current) return;
      console.error('useAdminAnalytics: fetch error', err);
      captureError(err, { hook: 'useAdminAnalytics', dateRange });
      setError('Erreur lors du chargement des données');
    } finally {
      if (myId === requestIdRef.current) setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
